import { useState, forwardRef } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { ChevronsUpDown, Check, X } from "lucide-react";

const LIST_CLS = "max-h-[300px] overflow-y-auto overscroll-contain";

const Trigger = forwardRef(({ testId, open, children, ...props }, ref) => (
  <button ref={ref} type="button" data-testid={testId} role="combobox" aria-expanded={open} {...props}
    className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring">
    {children}
  </button>
));

export const Combobox = ({ options, groups, value, onChange, placeholder, searchPlaceholder = "Escriba para buscar...", testId }) => {
  const [open, setOpen] = useState(false);
  const todasOpciones = groups ? groups.flatMap((g) => g.options) : (options || []);
  const sel = todasOpciones.find((o) => o.value === value);
  return (
    <Popover open={open} onOpenChange={setOpen} modal={true}>
      <PopoverTrigger asChild>
        <Trigger testId={testId} open={open}>
          <span className={`truncate text-left ${sel ? "text-slate-800" : "text-muted-foreground"}`}>
            {sel ? sel.label : placeholder}
          </span>
          <span className="flex items-center gap-1 shrink-0 ml-2">
            {sel && (
              <span data-testid={`${testId}-limpiar`} title="Limpiar" role="button"
                onClick={(e) => { e.stopPropagation(); onChange(""); }}
                className="text-slate-400 hover:text-slate-600"><X size={14} /></span>
            )}
            <ChevronsUpDown size={14} className="opacity-50" />
          </span>
        </Trigger>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start"
        onWheel={(e) => e.stopPropagation()}>
        <Command>
          <CommandInput placeholder={searchPlaceholder} data-testid={`${testId}-buscar`} />
          <CommandList className={LIST_CLS}>
            <CommandEmpty>Sin resultados</CommandEmpty>
            {groups
              ? groups.filter((g) => g.options.length > 0).map((g) => (
                  <CommandGroup key={g.heading} heading={g.heading}>
                    {g.options.map((o) => (
                      <CommandItem key={o.value} value={o.label}
                        onSelect={() => { onChange(o.value); setOpen(false); }}>
                        <Check size={14} className={`mr-2 shrink-0 ${o.value === value ? "opacity-100" : "opacity-0"}`} />
                        <span title={o.label} className="whitespace-normal break-words leading-snug min-w-0">{o.label}</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                ))
              : (
                <CommandGroup>
                  {options.map((o) => (
                    <CommandItem key={o.value} value={o.label}
                      onSelect={() => { onChange(o.value); setOpen(false); }}>
                      <Check size={14} className={`mr-2 shrink-0 ${o.value === value ? "opacity-100" : "opacity-0"}`} />
                      <span title={o.label} className="whitespace-normal break-words leading-snug min-w-0">{o.label}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export const MultiCombobox = ({ options, values = [], onChange, placeholder, searchPlaceholder = "Escriba para buscar...", testId }) => {
  const [open, setOpen] = useState(false);
  const toggle = (v) => onChange(values.includes(v) ? values.filter((x) => x !== v) : [...values, v]);
  return (
    <Popover open={open} onOpenChange={setOpen} modal={true}>
      <PopoverTrigger asChild>
        <Trigger testId={testId} open={open}>
          <span className={`truncate text-left ${values.length ? "text-slate-800" : "text-muted-foreground"}`}>
            {values.length ? `${values.length} seleccionado${values.length > 1 ? "s" : ""}` : placeholder}
          </span>
          <ChevronsUpDown size={14} className="opacity-50 shrink-0 ml-2" />
        </Trigger>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start"
        onWheel={(e) => e.stopPropagation()}>
        <Command>
          <CommandInput placeholder={searchPlaceholder} data-testid={`${testId}-buscar`} />
          <CommandList className={LIST_CLS}>
            <CommandEmpty>Sin resultados</CommandEmpty>
            <CommandGroup>
              {options.map((o) => (
                <CommandItem key={o.value} value={o.label} onSelect={() => toggle(o.value)}>
                  <Check size={14} className={`mr-2 shrink-0 ${values.includes(o.value) ? "opacity-100" : "opacity-0"}`} />
                  <span title={o.label} className="whitespace-normal break-words leading-snug min-w-0">{o.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
